// PageProvider.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

// Interface for the context
interface PageContextType {
  page: string;
  params: { [key: string]: string | undefined };
  navigate: (newPage: string, newParams?: { [key: string]: string }) => void;
  getParam: (name: string) => string | undefined;
  updateParams: (
    addParams?: { [key: string]: string },
    removeParams?: string[]
  ) => void;
  // A non-null message makes navigate() confirm before leaving; in-page
  // param updates are never guarded.
  setNavigationGuard: (message: string | null) => void;
}
interface PageProviderProps {
  children: ReactNode;
}
// Default context value
const defaultContextValue: PageContextType = {
  page: "",
  params: {},
  navigate: () => {},
  getParam: () => undefined,
  updateParams: () => {},
  setNavigationGuard: () => {},
};
// Create the context
const PageContext = createContext<PageContextType>(defaultContextValue);

// Provider component
export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const { stateParams } = useAuth();

  // Initialize page and params without any parameters (default domain)
  const [page, setPage] = useState<string>("");
  const [params, setParams] = useState<{ [key: string]: string | undefined }>(
    {}
  );

  // Helper function to create a query string from params, filtering out undefined values
  const createQueryString = (params: { [key: string]: string | undefined }) => {
    return new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as { [key: string]: string })
    ).toString();
  };
  const guardRef = useRef<string | null>(null);
  const setNavigationGuard = useCallback((message: string | null) => {
    guardRef.current = message;
  }, []);
  // Entries carry a rising index so a popstate says whether it went back or
  // forward; a refused pop is undone the other way, and the undo's own
  // popstate is swallowed by the skip flag.
  const historyIdxRef = useRef(0);
  const skipNextPopRef = useRef(false);

  // Function to navigate to a new page. `page` lives INSIDE params so that
  // later updateParams calls rebuild the URL without dropping it — otherwise a
  // hard refresh loses the page and lands on the main page.
  const navigate = useCallback(
    (newPage: string, newParams: { [key: string]: string } = {}) => {
      if (guardRef.current !== null && !window.confirm(guardRef.current)) return;
      setPage(newPage);
      const fullParams = { page: newPage, ...newParams };
      setParams(fullParams);
      historyIdxRef.current += 1;
      window.history.pushState(
        { idx: historyIdxRef.current },
        "",
        `?${createQueryString(fullParams)}`
      );
    },
    []
  );
  // Function to get a parameter from the params object
  const getParam = useCallback(
    (name: string): string | undefined => params[name],
    [params]
  );
  // Function to update the params object
  const updateParams = useCallback(
    (
      addParams: { [key: string]: string } = {},
      removeParams: string[] = []
    ) => {
      setParams((prevParams) => {
        // Clone the previous parameters
        const newParams = { ...prevParams };
        // Remove specified parameters
        removeParams.forEach((name) => {
          delete newParams[name];
        });

        // Add or update specified parameters
        Object.entries(addParams).forEach(([key, value]) => {
          newParams[key] = value;
        });

        const queryString = createQueryString(newParams);
        window.history.replaceState(
          { idx: historyIdxRef.current },
          "",
          `?${queryString}`
        );
        return newParams;
      });
    },
    [] // No dependencies needed because we're using functional updates
  );

  // Function to apply stateParams if they exist
  const applyStateParams = useCallback(() => {
    if (stateParams) {
      const parsedParams: { [key: string]: string | undefined } = {};
      const searchParams = new URLSearchParams(stateParams);
      searchParams.forEach((value, key) => {
        parsedParams[key] = value;
      });
      setPage(parsedParams["page"] || "defaultContent");
      setParams(parsedParams);
      const queryString = createQueryString(parsedParams);
      window.history.replaceState(
        { idx: historyIdxRef.current },
        "",
        `?${queryString}`
      );
    } else {
      // Temporary Fix
      const searchParams = new URLSearchParams(window.location.search);
      const parsedParams: { [key: string]: string | undefined } = {};
      searchParams.forEach((value, key) => {
        if (["id_token", "access_token", "session_state", "code", "state"].includes(key)) {
          return;
        }
        parsedParams[key] = value;
      });
      const nextPage = parsedParams["page"] || "defaultContent";
      setPage(nextPage);
      setParams(parsedParams);

      const queryString = createQueryString(parsedParams);
      if (queryString) {
        window.history.replaceState(
          { idx: historyIdxRef.current },
          "",
          `?${queryString}`
        );
      } else {
        window.history.replaceState(
          { idx: historyIdxRef.current },
          "",
          window.location.pathname
        );
      }
    }
  }, [stateParams]);

  // Apply stateParams on mount
  useEffect(() => {
    applyStateParams();

    const handlePopState = (event: PopStateEvent) => {
      if (skipNextPopRef.current) {
        skipNextPopRef.current = false;
        return;
      }
      const state = event.state as { idx?: number } | null;
      const nextIdx = typeof state?.idx === "number" ? state.idx : null;
      if (guardRef.current !== null && !window.confirm(guardRef.current)) {
        skipNextPopRef.current = true;
        // Undo the pop: a back goes forward again, a forward goes back.
        window.history.go(
          nextIdx !== null && nextIdx > historyIdxRef.current ? -1 : 1
        );
        return;
      }
      historyIdxRef.current = nextIdx ?? historyIdxRef.current;
      const searchParams = new URLSearchParams(window.location.search);
      const newParams: { [key: string]: string | undefined } = {};
      searchParams.forEach((value, key) => {
        newParams[key] = value;
      });
      setPage(newParams["page"] || "defaultContent");
      setParams(newParams);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [applyStateParams]);

  return (
    <PageContext.Provider
      value={{
        page,
        params,
        navigate,
        getParam,
        updateParams,
        setNavigationGuard,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePage = (): PageContextType => useContext(PageContext);
